import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { fmtDate } from "@/features/power_projects/utils";

import { useGroupTenders } from "./hooks";
import type { AnalyticsFilters, GroupTender } from "./types";

const money = (v: number | null | undefined) =>
  v == null ? "—" : `${new Intl.NumberFormat("pl-PL").format(Math.round(v))} zł`;
const asMult = (v: number | null | undefined) => (v == null ? "—" : `${v}%`);

function OutcomeBadge({ t }: { t: GroupTender }) {
  if (t.is_cancelled) return <Badge color="yellow">{t.status_display}</Badge>;
  if (t.is_won) return <Badge color="green">{t.status_display}</Badge>;
  return <Badge color="red">{t.status_display}</Badge>;
}

type SortKey = "multiplier" | "offer_value" | "title" | "decided_at" | "status";
type SortDir = "asc" | "desc";

// null values always sort to the bottom, whichever direction.
function cmp(a: GroupTender, b: GroupTender, key: SortKey, dir: SortDir): number {
  const sign = dir === "asc" ? 1 : -1;
  if (key === "title") return sign * (a.title || "").localeCompare(b.title || "", "pl");
  if (key === "status") return sign * (a.status || "").localeCompare(b.status || "", "pl");
  if (key === "decided_at") {
    const av = a.decided_at ?? "";
    const bv = b.decided_at ?? "";
    if (av === bv) return 0;
    if (!av) return 1;
    if (!bv) return -1;
    return sign * av.localeCompare(bv);
  }
  const av = a[key];
  const bv = b[key];
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  return sign * (av - bv);
}

const COLS: { key: SortKey; label: string; numeric?: boolean }[] = [
  { key: "title", label: "Przetarg" },
  { key: "status", label: "Wynik" },
  { key: "multiplier", label: "Mnożnik", numeric: true },
  { key: "offer_value", label: "Wartość", numeric: true },
  { key: "decided_at", label: "Rozstrzygnięto", numeric: true },
];

export function GroupTendersModal({
  dimension,
  category,
  title,
  filters,
  onClose,
}: {
  dimension: string;
  category: string;
  title: string;
  filters: AnalyticsFilters;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useGroupTenders(dimension, category, filters);
  const [sortKey, setSortKey] = useState<SortKey>("multiplier");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const items = useMemo(() => {
    const list = [...(data?.items ?? [])];
    list.sort((a, b) => cmp(a, b, sortKey, sortDir));
    return list;
  }, [data, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // Text sorts read best ascending; numeric/date default to highest-first.
      setSortDir(key === "title" || key === "status" ? "asc" : "desc");
    }
  };

  const counts = useMemo(() => {
    const list = data?.items ?? [];
    return {
      won: list.filter((t) => t.is_won).length,
      lost: list.filter((t) => !t.is_won && !t.is_cancelled).length,
      cancelled: list.filter((t) => t.is_cancelled).length,
    };
  }, [data]);

  return (
    <Modal open onClose={onClose} title={`${title} — ${category}`} size="xl">
      {isLoading && <Spinner />}
      {isError && <div className="text-sm text-red-600">Nie udało się wczytać listy przetargów.</div>}
      {data && (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-[var(--color-text-muted)]">
            <span>
              <b className="text-[var(--color-text-primary)]">{data.count}</b> przetargów
            </span>
            <span className="text-green-600">Wygrane {counts.won}</span>
            <span className="text-red-600">Przegrane {counts.lost}</span>
            <span className="text-amber-600">Anulowane {counts.cancelled}</span>
          </div>
          {items.length === 0 ? (
            <div className="py-8 text-center text-sm text-[var(--color-text-muted)]">
              Brak przetargów w tej grupie.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left">
                    {COLS.map((c) => (
                      <th
                        key={c.key}
                        onClick={() => toggleSort(c.key)}
                        className={`cursor-pointer select-none py-2 pr-3 font-mono text-[0.65rem] font-bold uppercase tracking-widest text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] ${
                          c.numeric ? "text-right" : ""
                        }`}
                      >
                        <span className={`inline-flex items-center gap-1 ${c.numeric ? "flex-row-reverse" : ""}`}>
                          {c.label}
                          {sortKey === c.key &&
                            (sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((t) => (
                    <tr key={t.id} className="border-t border-[var(--color-border)] align-top">
                      <td className="py-2 pr-3">
                        <Link
                          to={`/ze-tenders/${t.id}`}
                          onClick={onClose}
                          className="font-medium text-[var(--color-primary)] hover:underline"
                        >
                          {t.title}
                        </Link>
                        <div className="text-[11px] text-[var(--color-text-muted)]">
                          {[t.reference_number, t.client_name, t.oddzial || t.rejon]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </div>
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        <OutcomeBadge t={t} />
                      </td>
                      <td className="py-2 pr-3 text-right font-mono whitespace-nowrap">{asMult(t.multiplier)}</td>
                      <td className="py-2 pr-3 text-right font-mono whitespace-nowrap">{money(t.offer_value)}</td>
                      <td className="py-2 pr-3 text-right font-mono whitespace-nowrap text-[var(--color-text-muted)]">
                        {fmtDate(t.decided_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
